/* Cart-stepper quantity -> selling-plan swap + cap (plan 004).
 *
 * Fabio's model: quantity = months of supply = ship/rebill cadence. The PDP shows
 * max 3 tiles; 4-7 month plans are backend-only and reached by the cart "+" stepper.
 * Shopify's native cart stepper only changes QUANTITY (keeps the plan), so without
 * this you get e.g. "11 x 3 Months" instead of capping to "7 Months". This hook,
 * on a cart-line quantity change for an N-month-ladder subscription, snaps the line
 * to the tier matching the new quantity (qty N -> N-month plan), capped at the
 * product's max tier, and direction-aware over gaps (e.g. O4 has no 2-month tier).
 *
 * SAFE FOR EVERYTHING ELSE. It only acts when the line is (a) in the cart drawer,
 * (b) a subscription, (c) whose plan name is exactly "N Month(s)", and (d) whose
 * product has a >=2-tier N-month ladder (confirmed from product JSON). Anything
 * else — one-time products, plain products, other/single-plan subscriptions, O3
 * BOGO — is never intercepted (native cart stepper runs untouched). Even a line
 * that passes the quick check but turns out not to be a real ladder gets a plain
 * quantity change identical to native — never a wrong plan swap.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.SubCartQty = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Plan name must be EXACTLY "N Month(s)" — excludes O3 ("Buy 1"), money strings,
  // variant options, and "Deliver every 1 month"-style names.
  var MONTH_RE = /^(\d+)\s+months?$/i;

  /**
   * Pure: given the new target quantity and the product's tier ladder, return the
   * { qty, planId } the line should become. Caps at max tier, floors at min tier,
   * and snaps to a real tier — DOWN when the customer is decreasing, UP when
   * increasing — so gaps (e.g. O4: 1,3,4..7) behave sensibly in both directions.
   */
  function computeQtyCorrection(opts) {
    var currentQty = Number(opts.currentQty) || 0;
    var targetQty = Number(opts.targetQty) || 0;
    var tiers = (opts.ladder || [])
      .map(function (t) { return { months: Number(t.months), planId: t.planId }; })
      .sort(function (a, b) { return a.months - b.months; });
    if (!tiers.length) return null;

    var minT = tiers[0].months;
    var maxT = tiers[tiers.length - 1].months;
    var d = Math.max(minT, Math.min(targetQty, maxT)); // cap at max, floor at min

    var snapped;
    if (d <= currentQty) {
      // decreasing (or unchanged) -> snap DOWN to nearest tier <= d
      for (var i = tiers.length - 1; i >= 0; i--) {
        if (tiers[i].months <= d) { snapped = tiers[i]; break; }
      }
      if (!snapped) snapped = tiers[0];
    } else {
      // increasing -> snap UP to nearest tier >= d
      snapped = tiers.find(function (t) { return t.months >= d; }) || tiers[tiers.length - 1];
    }
    return { qty: snapped.months, planId: snapped.planId };
  }

  // ---- DOM / cart wiring (browser only) -------------------------------------
  if (typeof document === 'undefined') {
    return { computeQtyCorrection: computeQtyCorrection };
  }

  var ladderCache = {};
  function getLadder(handle) {
    if (ladderCache[handle]) return Promise.resolve(ladderCache[handle]);
    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var seen = {}, tiers = [];
        (p.selling_plan_groups || []).forEach(function (g) {
          (g.selling_plans || []).forEach(function (sp) {
            var m = MONTH_RE.exec((sp.name || '').trim());
            if (m) {
              var months = parseInt(m[1], 10);
              if (!seen[months]) { seen[months] = 1; tiers.push({ months: months, planId: sp.id }); }
            }
          });
        });
        tiers.sort(function (a, b) { return a.months - b.months; });
        ladderCache[handle] = tiers;
        return tiers;
      });
  }

  // Sync heuristic: does this cart line carry an "N Month(s)" plan label?
  function lineLooksLadder(lineEl) {
    if (!lineEl) return false;
    var opts = lineEl.querySelectorAll('.product-option');
    for (var i = 0; i < opts.length; i++) {
      if (MONTH_RE.test((opts[i].textContent || '').trim())) return true;
    }
    return false;
  }

  function itemsEl() {
    return document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
  }

  function changeUrl() {
    return (window.routes && window.routes.cart_change_url) || '/cart/change.js';
  }

  // Re-issue the change with (optionally) a selling_plan, then re-render the cart
  // sections via the theme's own cart element — matching how cart.js renders.
  function applyChange(line, quantity, sellingPlan) {
    var el = itemsEl();
    var sections = el ? el.getSectionsToRender() : [];
    var body = {
      line: line,
      quantity: quantity,
      sections: sections.map(function (s) { return s.section; }),
      sections_url: window.location.pathname,
    };
    if (sellingPlan) body.selling_plan = sellingPlan;
    return fetch(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) { return r.json(); })
      .then(function (parsed) {
        if (el && parsed.sections) {
          sections.forEach(function (s) {
            var container = document.getElementById(s.id);
            if (!container) return;
            var target = container.querySelector(s.selector) || container;
            target.innerHTML = el.getSectionInnerHTML(parsed.sections[s.section], s.selector);
          });
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sub-cart-qty', cartData: parsed });
        }
        return parsed;
      });
  }

  var busy = false;
  function handle(line, targetQty, input) {
    if (busy) return;
    busy = true;
    var done = function () { busy = false; };
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var item = (cart.items || [])[parseInt(line, 10) - 1];
        // Not a subscription -> plain quantity change (mirrors native).
        if (!item || !item.selling_plan_allocation) return applyChange(line, targetQty);
        var currentPlan = item.selling_plan_allocation.selling_plan.id;
        return getLadder(item.handle).then(function (ladder) {
          var isLadder = ladder.length >= 2 && ladder.some(function (t) {
            return String(t.planId) === String(currentPlan);
          });
          // Subscription but NOT a quantity ladder -> plain quantity change.
          if (!isLadder) return applyChange(line, targetQty);
          var fix = computeQtyCorrection({ currentQty: item.quantity, targetQty: targetQty, ladder: ladder });
          if (!fix) return applyChange(line, targetQty);
          if (input) input.value = String(fix.qty);
          return applyChange(line, fix.qty, fix.planId);
        });
      })
      .catch(function (e) {
        console.error('[sub-cart-qty]', e);
        return applyChange(line, targetQty).catch(function () {});
      })
      .then(done, done);
  }

  // PATH A — theme-native cart drawer. Fires only when a theme `.quantity__input`
  // emits `change` (Dawn/Revive drawer). UpCart never fires this (see PATH B).
  document.addEventListener('change', function (evt) {
    var input = evt.target;
    if (!input || !input.matches || !input.matches('.quantity__input')) return;
    var lineEl = input.closest('.cart-item');
    if (!lineEl || !input.closest('cart-drawer, cart-drawer-items, cart-items')) return;
    if (!lineLooksLadder(lineEl)) return; // only N-month-ladder subscription lines
    var line = input.dataset.index;
    var targetQty = parseInt(input.value, 10);
    if (!line || !(targetQty > 0)) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
    handle(line, targetQty, input);
  }, true);

  // ---- PATH B — UpCart (AfterSell) drawer ----------------------------------
  // UpCart renders its OWN drawer, bypasses the theme cart, and dispatches NO
  // line-item quantity event — so PATH A's `change` listener never runs in the
  // real drawer (this was the bug: qty 6 stayed on the "3 Months" plan). UpCart's
  // only supported hook is `upcartSubscribeCartLoaded`, which it RE-FIRES after
  // every drawer re-render, including its own +/- stepper. On each fire we re-scan
  // the cart and, if a ladder sub line's plan no longer matches its quantity, issue
  // ONE corrective /cart/change.js. Direction is derived statelessly from the line's
  // CURRENT plan tier vs its new qty (no per-line state to lose). The fixpoint —
  // once plan matches qty we no-op — is the guard against our own re-render looping.
  // Fully fail-safe: if anything throws, UpCart's cart is left exactly as it was.

  function changeLineDirect(line, quantity, sellingPlan) {
    var body = { line: line, quantity: quantity };
    if (sellingPlan) body.selling_plan = sellingPlan;
    return fetch(changeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  // Own lock (decoupled from PATH A's `busy`). `rescan` coalesces a render that
  // arrives mid-flight so the FINAL state is always reconciled — without it, a
  // cartLoaded firing during an in-flight pass is dropped and the line can be left
  // on the wrong plan (e.g. qty 6 stuck on "5 Months" after rapid + clicks).
  var rbusy = false, rescan = false;
  function reconcileLadderLines() {
    if (rbusy) { rescan = true; return; }
    rbusy = true;
    var done = function () {
      rbusy = false;
      if (rescan) { rescan = false; reconcileLadderLines(); } // trailing pass heals final state
    };
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var pick = { line: -1, fix: null }; // fix only the FIRST mismatch per pass
        var lookups = items.map(function (item, i) {
          // MAX means two Day bottles per monthly refill, not a two-month supply plan.
          if (item.properties && item.properties._gnd_source === 'definitive-pdp' && item.properties._gnd_group) return Promise.resolve();
          var alloc = item.selling_plan_allocation;
          if (!alloc || !alloc.selling_plan) return Promise.resolve(); // not a sub
          var planId = alloc.selling_plan.id;
          var m = MONTH_RE.exec((alloc.selling_plan.name || '').trim());
          if (!m) return Promise.resolve(); // not an "N Month(s)" plan (O3 BOGO etc.)
          var planMonths = parseInt(m[1], 10);
          return getLadder(item.handle).then(function (ladder) {
            var onLadder = ladder.length >= 2 && ladder.some(function (t) {
              return String(t.planId) === String(planId);
            });
            if (!onLadder) return; // single-plan / other subscription -> untouched
            // currentQty = the line's existing plan tier => snap UP when qty rose
            // above it, DOWN when it dropped below it (handles O4's 2-month gap).
            var fix = computeQtyCorrection({ currentQty: planMonths, targetQty: item.quantity, ladder: ladder });
            if (!fix) return;
            var needs = String(fix.planId) !== String(planId) || fix.qty !== item.quantity;
            if (needs && pick.line === -1) { pick.line = i + 1; pick.fix = fix; }
          });
        });
        return Promise.all(lookups).then(function () {
          if (pick.line === -1) return; // fixpoint reached: every ladder line is correct
          return changeLineDirect(pick.line, pick.fix.qty, pick.fix.planId).then(function () {
            // nudge UpCart to repaint its drawer if it exposes the helper
            if (typeof window.upcartRefreshCart === 'function') {
              try { window.upcartRefreshCart(); } catch (e) {}
            }
          });
        });
      })
      .catch(function (e) { console.error('[sub-cart-qty] reconcile', e); })
      .then(done, done);
  }

  // Debounce so a burst of +/- clicks (each fires CartUpdated+CartLoaded, as does
  // our own corrective change) collapses into one reconcile after the burst settles.
  // The fixpoint (plan matches qty => no-op, no change => no further events) stops it.
  var reconcileTimer = null;
  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () { reconcileTimer = null; reconcileLadderLines(); }, 200);
  }
  function subscribeUpcart() {
    if (typeof window.upcartSubscribeCartLoaded !== 'function') return false;
    // CartLoaded re-fires after every UpCart render (incl. its own +/- stepper);
    // CartUpdated also fires on this store — subscribe to both when present so a
    // change is never missed (duplicate triggers are harmless via the fixpoint).
    window.upcartSubscribeCartLoaded(scheduleReconcile);
    if (typeof window.upcartSubscribeCartUpdated === 'function') {
      window.upcartSubscribeCartUpdated(scheduleReconcile);
    }
    return true;
  }

  // UpCart is an app embed and may load after this script — poll briefly to bind.
  if (!subscribeUpcart()) {
    var upcartTries = 0;
    var upcartIv = setInterval(function () {
      if (subscribeUpcart() || ++upcartTries > 60) clearInterval(upcartIv); // ~15s grace
    }, 250);
  }

  return { computeQtyCorrection: computeQtyCorrection, reconcileLadderLines: reconcileLadderLines };
});
